/**
 * Everything the HUD shows about a kingdom, derived from server state in one
 * pass.
 *
 * The rule this file exists to honour: **the client never reimplements game
 * math.** Every number below comes out of `kngdm-shared` — `hexYield`,
 * `workerEfficiency`, `workerCount`, `troopCount`, `storageCap`, `gloryScore`,
 * `FOOD_UPKEEP_PER_POP`. What this module contributes is the *aggregation*: the
 * server never sends "your net food per second", it sends hexes and kingdoms,
 * and the panels would each have to fold that up themselves otherwise.
 *
 * Because it is a fold over shared functions rather than a second model, a
 * balance change in `balance.ts` moves the HUD with it and cannot desync from
 * what the simulation actually does.
 *
 * Pure and DOM-free, so the arithmetic is testable without a browser.
 */

import type {
  BuildingType,
  GloryScore,
  KingdomId,
  KingdomState,
  MapState,
  ResourceBundle,
  ResourceType,
} from "kngdm-shared";
import {
  FOOD_UPKEEP_PER_POP,
  RESOURCE_TYPES,
  clamp01,
  emptyBundle,
  gloryScore,
  hexYield,
  buildingStorageBonus,
  resourceCap,
  storageCap,
  troopCount,
  workerCount,
  workerEfficiency,
} from "kngdm-shared";

/** One row of the resource bar. */
export interface ResourceLine {
  readonly type: ResourceType;
  readonly amount: number;
  /** Net per second: for food this is already minus population upkeep. */
  readonly perSecond: number;
  readonly cap: number;
  /** Room left before the cap silently starts eating production. */
  readonly headroom: number;
  /** `amount / cap`, 0..1 — how close the bar is to wasting yield. */
  readonly capFraction: number;
}

export interface KingdomSummary {
  readonly kingdomId: KingdomId;
  readonly name: string;
  /** 0xRRGGBB, as it arrives on the wire. */
  readonly color: number;
  readonly eliminated: boolean;
  readonly isBot: boolean;

  readonly territory: number;
  readonly buildingCount: number;

  readonly population: number;
  readonly workers: number;
  readonly troops: number;
  readonly workerRatio: number;
  /** 0..1 share of owned land actually being worked. */
  readonly efficiency: number;

  readonly storageCap: number;
  readonly foodUpkeep: number;
  /** What the kingdom currently holds — the input to every affordability check. */
  readonly banked: ResourceBundle;
  /** Per-second production after worker coverage, before food upkeep. */
  readonly grossYield: ResourceBundle;
  /** Per-second production after worker coverage and food upkeep. */
  readonly netYield: ResourceBundle;
  readonly resources: readonly ResourceLine[];

  readonly glory: GloryScore;
}

/**
 * Fold the map and kingdom list into everything the panels need.
 *
 * An unknown or eliminated-and-removed kingdom id yields a zeroed summary
 * rather than throwing: this runs inside the render loop ten times a second,
 * and a missing kingdom must degrade to an empty HUD, not a dead frame.
 */
export function deriveKingdomView(
  map: MapState,
  kingdoms: readonly KingdomState[],
  id: KingdomId,
): KingdomSummary {
  const kingdom = kingdoms.find((candidate) => candidate.id === id);

  let territory = 0;
  const buildings: BuildingType[] = [];
  let specialHexes = 0;
  let storageBonus = 0;
  const gross = emptyBundle();

  for (const hex of map.hexes) {
    if (hex.owner !== id) continue;
    territory += 1;
    if (hex.building !== null) buildings.push(hex.building);
    if (hex.special !== null) specialHexes += 1;
    storageBonus += buildingStorageBonus(hex.building);

    const perHex = hexYield(hex.biome, hex.building, hex.special);
    for (const type of RESOURCE_TYPES) gross[type] += perHex[type];
  }

  const population = kingdom?.population ?? 0;
  const workerRatio = kingdom?.workerRatio ?? 0;
  const workers = workerCount(population, workerRatio);
  const troops = troopCount(population, workerRatio);

  // Land you have no workers for dilutes the land you do — the coupling that
  // makes expansion a decision. Applied to the whole kingdom, exactly as the
  // simulation applies it.
  const efficiency = workerEfficiency(workers, territory);
  for (const type of RESOURCE_TYPES) gross[type] *= efficiency;

  const foodUpkeep = population * FOOD_UPKEEP_PER_POP;
  const netYield: ResourceBundle = { ...gross, food: gross.food - foodUpkeep };

  const cap = storageCap(territory, storageBonus);
  const banked = kingdom?.resources ?? emptyBundle();

  // `resourceCap`, not `cap`: Aether's ceiling is flat, and reporting the
  // territory-scaled one would promise headroom the simulation will not honour —
  // and would never show the "you are wasting production" warning that is the
  // whole reason this panel tracks cap proximity.
  const resources: ResourceLine[] = RESOURCE_TYPES.map((type) => {
    const lineCap = resourceCap(type, territory, storageBonus);
    return {
      type,
      amount: banked[type],
      perSecond: netYield[type],
      cap: lineCap,
      headroom: Math.max(0, lineCap - banked[type]),
      capFraction: lineCap > 0 ? clamp01(banked[type] / lineCap) : 0,
    };
  });

  return {
    kingdomId: id,
    name: kingdom?.name ?? "—",
    color: kingdom?.color ?? 0x999999,
    eliminated: kingdom?.eliminated ?? false,
    isBot: kingdom?.isBot ?? false,

    territory,
    buildingCount: buildings.length,

    population,
    workers,
    troops,
    workerRatio,
    efficiency,

    storageCap: cap,
    foodUpkeep,
    banked,
    grossYield: gross,
    netYield,
    resources,

    // Trade volume is not on the wire because trade does not exist yet; the
    // server scores it as 0 too, so this matches rather than guesses.
    glory: gloryScore({
      kingdomId: id,
      territoryCount: territory,
      buildings,
      specialHexes,
      banked,
      tradeVolume: 0,
    }),
  };
}
